import { Component } from '@theme/component';

const normalise = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('en-GB').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
const ignored = new Set(['your','my','the','a','an','to','for','how','can','i','do','where','is','of','with','please','our','me']);
const searchTerms = query => query.split(' ').filter(word => word && !ignored.has(word)).map(word => word.replace(/(ing|ed|s)$/,'')).filter(Boolean);

class MEGHelpCentre extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.querySelector('[data-care-search]');
    this.input = this.querySelector('[data-care-input]');
    this.results = this.querySelector('[data-care-results]');
    this.empty = this.querySelector('[data-care-empty]');
    this.groups = [...this.querySelectorAll('[data-care-group]')];
    this.config = JSON.parse(this.querySelector('[data-care-config]').textContent);
    this.suggestions = this.querySelector('[data-care-suggestions]');
    this.suggestionList = this.querySelector('[data-care-suggestion-list]');
    this.suggestionStatus = this.querySelector('[data-care-suggestion-status]');
    this.input.addEventListener('input', this.filter, { signal });
    this.form.addEventListener('submit', event => {
      event.preventDefault();
      this.filter();
      if (!normalise(this.input.value)) { this.input.focus(); return; }
      const first = this.suggestionList.querySelector('a[href^="#help-"]');
      if (first) { first.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); first.focus({ preventScroll: true }); }
      else { this.empty.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
    }, { signal });
    this.form.addEventListener('reset', event => {
      event.preventDefault();
      this.input.value = '';
      this.filter();
      this.input.focus({ preventScroll: true });
    }, { signal });
    this.addEventListener('click', event => {
      const link = event.target instanceof Element ? event.target.closest('a[href^="#help-"]') : null;
      if (!link) return;
      this.input.value = '';
      this.filter();
    }, { signal });
    window.addEventListener('hashchange', () => this.revealHash(), { signal });
    if (document.readyState === 'complete') requestAnimationFrame(() => this.revealHash());
    else window.addEventListener('load', () => this.revealHash(), { once: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
  }

  revealHash() {
    const hash = location.hash;
    if (!/^#help-(?:top|tracking|delivery|orders|returns|shopping|payments|contact)$/.test(hash)) return;
    const target = this.querySelector(hash);
    if (!target) return;
    this.input.value = '';
    this.filter();
    requestAnimationFrame(() => {
      if (!this.isConnected || location.hash !== hash) return;
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
      if (target.hasAttribute('tabindex')) target.focus({ preventScroll: true });
    });
  }

  filter = () => {
    const query = normalise(this.input.value);
    const terms = searchTerms(query);
    const searching = query.length > 0;
    const tracking = /\b(track|tracking|tracked|parcel|courier|shipment)\b/.test(query);
    const found = [];
    this.suggestions.hidden = !searching;
    this.suggestionList.replaceChildren();
    if (searching && !this.beforeSearch) this.beforeSearch = new Set([...this.querySelectorAll('[data-care-answer][open]')]);
    let count = 0;
    for (const group of this.groups) {
      let matches = 0;
      for (const answer of group.querySelectorAll('[data-care-answer]')) {
        const content = normalise(answer.textContent);
        const topic = group.dataset.careTopic;
        const matchingTerms = terms.length > 0 && terms.every(term => content.includes(term) || normalise(topic).includes(term) || (term === 'parcel' && /\b(track|delivery|order)\b/.test(content)));
        const relatedTracking = tracking && topic === 'delivery' && /\b(track|tracking|parcel|courier|shipment)\b/.test(content);
        const show = !searching || matchingTerms || relatedTracking;
        answer.hidden = !show;
        if (searching) {
          answer.open = show;
          if (show) found.push({ href: '#help-' + topic, title: answer.querySelector('summary').textContent.replace(/\+\s*$/, '').trim(), topic });
        } else if (this.beforeSearch) answer.open = this.beforeSearch.has(answer);
        if (show) matches++;
      }
      group.hidden = matches === 0;
      count += matches;
    }
    if (!searching) this.beforeSearch = null;
    this.empty.hidden = count !== 0 || tracking;
    this.results.textContent = searching ? (tracking ? 'Parcel-tracking guide and ' + count + ' related answers.' : this.config.results.replace('__COUNT__', String(count))) : this.config.all;
    if (!searching) return;
    const links = tracking ? [{ href: '#help-tracking', title: 'Track your parcel', description: 'Find your tracking number and choose your delivery carrier.' }] : [];
    const seen = new Set();
    const topicScore = result => terms.filter(term => normalise(result.topic).includes(term)).length;
    const ranked = [...found].sort((a, b) => topicScore(b) - topicScore(a));
    for (const result of ranked) {
      if (seen.has(result.topic) || links.length >= 4) continue;
      seen.add(result.topic);
      links.push({ href: result.href, title: result.title, description: 'See the ' + result.topic + ' help answers.' });
    }
    this.suggestionStatus.textContent = links.length ? 'Recommended help on this page' : 'No direct matches yet';
    if (!links.length) {
      const note = document.createElement('p'); note.textContent = 'Try “track parcel”, “delivery”, “returns” or “payments”, or contact our support team below.'; this.suggestionList.append(note);
    }
    for (const result of links) {
      const link = document.createElement('a'); link.className = 'meg-care__suggestion'; link.href = result.href;
      const title = document.createElement('strong'); title.textContent = result.title;
      const description = document.createElement('span'); description.textContent = result.description;
      const arrow = document.createElement('b'); arrow.setAttribute('aria-hidden','true'); arrow.textContent = '↗';
      link.append(title, description, arrow); this.suggestionList.append(link);
    }
  };
}

if (!customElements.get('meg-help-centre')) customElements.define('meg-help-centre', MEGHelpCentre);

